<script setup lang="ts">
import type {
  Block,
  Collaborator,
  CustomBlock as CustomBlockType,
  PopupDisplayAnimation,
  PopupScreenPosition,
  TemplateContent,
  ViewportSize,
} from "@aswin.dev/types";
import { filterBlockList } from "@aswin.dev/core";
import { normalizeTemplateContentPages } from "@aswin.dev/types";
import { ImageUp, Sparkles, SquarePlus } from "@lucide/vue";
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  watch,
  type Component,
} from "vue";
import { useWindowSize } from "@vueuse/core";
import draggable from "vuedraggable";
import { useCloudI18n } from "../composables/useCloudI18n";
import { useI18n } from "../composables/useI18n";
import {
  BLOCK_REGISTRY_KEY,
  CAPABILITIES_KEY,
  CONDITION_PREVIEW_KEY,
  EDITOR_KEY,
  requireInject,
} from "../keys";
import { resolveBlockComponent } from "../utils/blockComponentResolver";
import { readableTextColor } from "../utils/readableTextColor";
import { resolvePopupEmbed } from "../utils/resolvePopupEmbed";

import BlockWrapper from "./blocks/BlockWrapper.vue";
import ButtonBlock from "./blocks/ButtonBlock.vue";
import CountdownBlockComponent from "./blocks/CountdownBlock.vue";
import CustomBlock from "./blocks/CustomBlock.vue";
import DiscountCouponBlock from "./blocks/DiscountCouponBlock.vue";
import DividerBlock from "./blocks/DividerBlock.vue";
import FormBlockComponent from "./blocks/FormBlock.vue";
import HtmlBlock from "./blocks/HtmlBlock.vue";
import ImageBlock from "./blocks/ImageBlock.vue";
import InputBlock from "./blocks/InputBlock.vue";
import MenuBlock from "./blocks/MenuBlock.vue";
import ParagraphBlock from "./blocks/ParagraphBlock.vue";
import SectionBlock from "./blocks/SectionBlock.vue";
import SocialIconsBlock from "./blocks/SocialIconsBlock.vue";
import SpacerBlock from "./blocks/SpacerBlock.vue";
import TableBlock from "./blocks/TableBlock.vue";
import TitleBlock from "./blocks/TitleBlock.vue";
import VideoBlock from "./blocks/VideoBlock.vue";

const blockComponentMap: Record<string, Component> = {
  section: SectionBlock,
  title: TitleBlock,
  paragraph: ParagraphBlock,
  image: ImageBlock,
  button: ButtonBlock,
  input: InputBlock,
  divider: DividerBlock,
  spacer: SpacerBlock,
  html: HtmlBlock,
  social: SocialIconsBlock,
  menu: MenuBlock,
  table: TableBlock,
  video: VideoBlock,
  countdown: CountdownBlockComponent,
  form: FormBlockComponent,
  discountCoupon: DiscountCouponBlock,
  custom: CustomBlock,
};

const props = defineProps<{
  viewport: ViewportSize;
  content: TemplateContent;
  selectedBlockId: string | null;
  darkMode: boolean;
  previewMode: boolean;
  lockedBlocks?: Map<string, Collaborator>;
  /** Multi-step canvas (e.g. popup flows); enable via editor config `multiPageCanvas`. */
  multiPageCanvas?: boolean;
}>();

const emit = defineEmits<{
  (e: "select-block", blockId: string | null): void;
  (e: "open-ai-chat"): void;
  (e: "open-design-reference"): void;
}>();

const { t } = useI18n();
const { t: cloudT } = useCloudI18n();

const editor = requireInject(EDITOR_KEY, "Canvas");
const conditionPreview = inject(CONDITION_PREVIEW_KEY, null);
const blockRegistry = inject(BLOCK_REGISTRY_KEY, null);

const caps = inject(CAPABILITIES_KEY, {});

const canUseAiChat = computed(
  () =>
    (caps.plan?.hasFeature("ai_generation") ?? false) &&
    (caps.ai?.isFeatureEnabled("chat") ?? false),
);
const canUseDesignToTemplate = computed(
  () =>
    (caps.plan?.hasFeature("ai_generation") ?? false) &&
    (caps.ai?.isFeatureEnabled("designToTemplate") ?? false),
);

const blocks = computed({
  get: () => props.content.blocks,
  set: (value: unknown[]) => {
    const nextBlocks = filterBlockList(value);
    const c = props.content;
    const activeId = c.activeCanvasPageId;
    if (c.canvasPages?.length && activeId) {
      editor.setContent(
        normalizeTemplateContentPages({
          ...c,
          canvasPages: c.canvasPages.map((p) =>
            p.id === activeId ? { ...p, blocks: nextBlocks } : p,
          ),
          blocks: nextBlocks,
          activeCanvasPageId: activeId,
        }),
      );
      return;
    }
    editor.setContent({
      ...c,
      blocks: nextBlocks,
    });
  },
});

onMounted(() => {
  if (props.multiPageCanvas) {
    editor.ensureCanvasPages();
  }
});

const viewportWidth = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return 375;
    case "tablet":
      return 768;
    default:
      return props.content.settings.width;
  }
});

const hasCanvasPages = computed(
  () => (props.content.canvasPages ?? []).length > 0,
);

/**
 * Step strip is rendered externally by Editor.vue (via CanvasPagesNav) so the
 * canvas itself never owns the step UI. We just need to know whether to leave
 * vertical space at the top for it.
 */
const showStepNav = computed(
  () => props.multiPageCanvas === true && hasCanvasPages.value,
);

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/**
 * Compose a CSS background colour value from a hex colour and an optional
 * alpha (0..1). Mirrors the popup runtime helper in `index.html` (search
 * `popupBackgroundCss`) so the editor preview reflects the same blending
 * the embed uses. Falls back to the raw colour string when the input isn't
 * a parseable 3- or 6-digit hex (e.g. CSS variable, named colour) — leaving
 * the existing rendering untouched in those cases.
 */
function composeBackgroundColor(
  color: string | undefined,
  opacity: number | undefined,
): string | undefined {
  if (!color) return color;
  const a = opacity === undefined || !Number.isFinite(opacity) ? 1 : opacity;
  const alpha = Math.min(1, Math.max(0, a));
  if (alpha === 1) return color;
  const raw = color.trim();
  let hex: string | null = null;
  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    hex = raw.slice(1);
  } else if (/^#[0-9a-f]{3}$/i.test(raw)) {
    hex = raw[1] + raw[1] + raw[2] + raw[2] + raw[3] + raw[3];
  }
  if (!hex) return color;
  const n = Number.parseInt(hex, 16);
  if (!Number.isFinite(n)) return color;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Compose a soft drop shadow whose blur, offset, and alpha all scale with the
 * 0..1 `intensity` value driven by `backgroundShadow`. Returning `undefined`
 * means "no opinion" so the caller can fall back to its existing default
 * shadow (preserving legacy behaviour for templates that never set the field).
 */
function composeBoxShadow(intensity: number | undefined): string | undefined {
  if (intensity === undefined || !Number.isFinite(intensity)) return undefined;
  const t = Math.min(1, Math.max(0, intensity));
  if (t === 0) return "none";
  const yOffset = Math.round(t * 12);
  const blur = Math.round(t * 60);
  const alpha = Number((t * 0.35).toFixed(3));
  return `0 ${yOffset}px ${blur}px rgba(0, 0, 0, ${alpha})`;
}

const popupEmbedDesign = computed(() =>
  props.content.settings.popup
    ? resolvePopupEmbed(props.content.settings).design
    : null,
);

/** Default blur when no value is stored (mirrors {@link createDefaultPopupEmbedSettings}). */
const POPUP_OVERLAY_DEFAULT_BLUR_PX = 8;

function clampBlurPx(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n)) {
    return POPUP_OVERLAY_DEFAULT_BLUR_PX;
  }
  return Math.min(30, Math.max(0, n));
}

const popupOverlayBackdrop = computed(() => {
  const d = popupEmbedDesign.value;
  return d?.useOverlay ? d : null;
});

const popupSizePreset = computed(
  () => popupEmbedDesign.value?.sizePreset ?? "large",
);

/** Tall edge-to-edge panel (fixed width, viewport height). */
const isPopupFullHeight = computed(
  () =>
    Boolean(popupOverlayBackdrop.value) &&
    popupSizePreset.value === "fullHeight",
);

/** Covers the entire preview viewport. */
const isPopupFullscreen = computed(
  () =>
    Boolean(popupOverlayBackdrop.value) &&
    popupSizePreset.value === "fullscreen",
);

const isPopupViewportSized = computed(
  () => isPopupFullHeight.value || isPopupFullscreen.value,
);

// ---------------------------------------------------------------------------
// Popup background image — auto-fit aspect-ratio probing
// ---------------------------------------------------------------------------
// Mirrors the runtime in `index.html` (search "ADOPT IMAGE NATURAL ASPECT
// RATIO"): when the user picks `imageFit: "auto"` the popup card adopts the
// image's natural aspect ratio so the artwork fills the card edge-to-edge
// instead of sitting as a centred band on a wider card. We probe via an
// in-memory `<img>` so the editor preview stays in sync with the URL the
// user just picked, and bail gracefully if the probe fails.
const popupBgImageRatio = ref<number | null>(null);

watch(
  () => {
    const d = popupEmbedDesign.value;
    return d?.backgroundImage && d.backgroundImageUrl
      ? d.backgroundImageUrl
      : "";
  },
  (url) => {
    popupBgImageRatio.value = null;
    if (!url) return;
    const probe = new Image();
    const requestedUrl = url;
    probe.onload = () => {
      // Bail if a newer URL has been picked while we were loading.
      if (requestedUrl !== popupEmbedDesign.value?.backgroundImageUrl) return;
      if (!probe.naturalWidth || !probe.naturalHeight) return;
      popupBgImageRatio.value = probe.naturalWidth / probe.naturalHeight;
    };
    probe.onerror = () => {
      popupBgImageRatio.value = null;
    };
    probe.src = url;
  },
  { immediate: true },
);

// Reactive viewport height — drives the auto-fit cap so that a very tall
// (portrait) background image can't push the popup card past the visible
// canvas area. Matches what mainstream popup builders do: shrink the card
// proportionally so the entire popup stays in view, instead of forcing the
// editor to scroll vertically just to see the bottom of the design.
const { height: windowHeight } = useWindowSize();

/**
 * Pixels of editor chrome above and below the popup canvas (top toolbar,
 * popup-frame padding, viewport toggle, etc). Subtracted from the window
 * height to derive the available space for the popup card itself. Tuned to
 * leave a small breathing margin so the card doesn't touch the toolbar.
 */
const POPUP_CARD_VIEWPORT_CHROME_PX = 160;
/** Floor — never let the cap collapse below this even on tiny windows. */
const POPUP_CARD_MIN_AVAILABLE_HEIGHT_PX = 320;

const popupCardMaxHeight = computed(() =>
  Math.max(
    POPUP_CARD_MIN_AVAILABLE_HEIGHT_PX,
    windowHeight.value - POPUP_CARD_VIEWPORT_CHROME_PX,
  ),
);

/**
 * In popup overlay mode the canvas card is rendered inside a larger viewport-
 * like frame so the dark backdrop is visible around it. On desktop the frame
 * stretches to fill the container (so the browser-chrome wrapper feels like a
 * real screen); on mobile/tablet it stays at a fixed phone/tablet width.
 */
const popupFrameFillsContainer = computed(() => props.viewport === "desktop");

/** Numeric fallback for tablet/mobile. */
const popupFrameWidth = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return 375;
    case "tablet":
      return 768;
    default:
      return 0;
  }
});

const popupFrameMinHeight = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return 600;
    case "tablet":
      return 600;
    default:
      return 600;
  }
});

const popupCardWidth = computed(() => {
  const settingsWidth = props.content.settings.width;
  if (popupFrameFillsContainer.value) {
    return settingsWidth;
  }
  return Math.min(settingsWidth, popupFrameWidth.value);
});

function popupHorizontalJustifyClass(position: PopupScreenPosition): string {
  switch (position) {
    case "topLeft":
    case "middleLeft":
    case "bottomLeft":
      return "tpl:justify-start";
    case "topRight":
    case "middleRight":
    case "bottomRight":
      return "tpl:justify-end";
    case "topCenter":
    case "bottomCenter":
    case "center":
    default:
      return "tpl:justify-center";
  }
}

const popupCardAlignClass = computed(() => {
  const position = popupOverlayBackdrop.value?.popupPosition ?? "center";

  if (isPopupFullscreen.value) {
    return "tpl:items-stretch tpl:justify-center";
  }

  if (isPopupFullHeight.value) {
    return `tpl:items-stretch ${popupHorizontalJustifyClass(position)}`;
  }

  switch (position) {
    case "topLeft":
      return "tpl:items-start tpl:justify-start";
    case "topCenter":
      return "tpl:items-start tpl:justify-center";
    case "topRight":
      return "tpl:items-start tpl:justify-end";
    case "middleLeft":
      return "tpl:items-center tpl:justify-start";
    case "middleRight":
      return "tpl:items-center tpl:justify-end";
    case "bottomLeft":
      return "tpl:items-end tpl:justify-start";
    case "bottomCenter":
      return "tpl:items-end tpl:justify-center";
    case "bottomRight":
      return "tpl:items-end tpl:justify-end";
    case "center":
    default:
      return "tpl:items-center tpl:justify-center";
  }
});

const popupFrameStyle = computed(() => {
  const base = {
    width: popupFrameFillsContainer.value
      ? "100%"
      : `${popupFrameWidth.value}px`,
  };
  if (isPopupViewportSized.value) {
    const h = popupCardMaxHeight.value;
    return {
      ...base,
      minHeight: `${h}px`,
      height: `${h}px`,
    };
  }
  return {
    ...base,
    minHeight: `${popupFrameMinHeight.value}px`,
  };
});

const canvasCardStyle = computed(() => {
  const inPopupFrame = !!popupOverlayBackdrop.value;
  const width = inPopupFrame
    ? `${popupCardWidth.value}px`
    : showStepNav.value
      ? "100%"
      : `${viewportWidth.value}px`;

  // Popup background image — mirrors the embed runtime in
  // packages/editor/index.html (cssBackgroundUrl + imageFit). Only painted
  // when popup mode is active, the toggle is on, and a URL is set, so non-
  // popup canvases stay completely unaffected.
  const popupDesign = popupOverlayBackdrop.value;
  const popupBgUrl =
    popupDesign?.backgroundImage && popupDesign.backgroundImageUrl
      ? popupDesign.backgroundImageUrl
      : "";
  const popupBgFit = popupDesign?.backgroundImageFit ?? "cover";
  // For `auto` fit we let the popup card adopt the image's natural aspect
  // ratio (probed asynchronously in `popupBgImageRatio`) and stretch the
  // image to fill it perfectly. While the probe is pending we fall back to
  // `cover` so the artwork still fills the card instead of sitting at its
  // native pixel size as a tiny centred band.
  const autoFitWithRatio =
    popupBgUrl && popupBgFit === "auto" && popupBgImageRatio.value !== null;
  const popupBgSize = autoFitWithRatio
    ? "100% 100%"
    : popupBgFit === "contain"
      ? "contain"
      : "cover";

  // When auto-fit is active, derive both width AND height from the image's
  // natural ratio AND the available viewport, so the entire popup stays in
  // view without scrolling. If the natural height would push past the
  // viewport cap (typical with portrait images), scale the width down
  // proportionally instead — the image still fills the card cleanly because
  // background-size is `100% 100%` against a card whose ratio matches.
  let autoFitWidthPx: number | null = null;
  let autoFitHeightPx: number | null = null;
  // Portrait background auto-fit is skipped for viewport-sized presets — those
  // modes intentionally fill the preview height instead of shrinking to the
  // image's natural aspect ratio.
  if (autoFitWithRatio && inPopupFrame && !isPopupViewportSized.value) {
    const ratio = popupBgImageRatio.value as number;
    const baseWidth = popupCardWidth.value;
    const naturalHeight = baseWidth / ratio;
    if (naturalHeight > popupCardMaxHeight.value) {
      autoFitHeightPx = popupCardMaxHeight.value;
      autoFitWidthPx = autoFitHeightPx * ratio;
    } else {
      autoFitWidthPx = baseWidth;
      autoFitHeightPx = naturalHeight;
    }
  }

  const customShadow = composeBoxShadow(
    props.content.settings.backgroundShadow,
  );
  const style: Record<string, string | undefined> = {
    width: autoFitWidthPx !== null ? `${Math.round(autoFitWidthPx)}px` : width,
    backgroundColor: composeBackgroundColor(
      props.content.settings.backgroundColor,
      props.content.settings.backgroundOpacity,
    ),
    backgroundImage: popupBgUrl ? `url("${popupBgUrl}")` : undefined,
    backgroundSize: popupBgUrl ? popupBgSize : undefined,
    backgroundPosition: popupBgUrl ? "center" : undefined,
    backgroundRepeat: popupBgUrl ? "no-repeat" : undefined,
    boxShadow: props.darkMode
      ? "none"
      : (customShadow ?? "var(--tpl-shadow-xl)"),
    filter: props.darkMode ? "invert(1) hue-rotate(180deg)" : "none",
    transition:
      "width 300ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 300ms ease",
  };
  if (autoFitHeightPx !== null) {
    // Lock to the computed height and drop the min-height so the card
    // collapses cleanly to the image's footprint. Setting both width and
    // height directly (instead of `aspectRatio`) lets us cap the height
    // without losing the proportional width scale-down for tall images.
    style.height = `${Math.round(autoFitHeightPx)}px`;
    style.minHeight = "0";
  }

  if (inPopupFrame && isPopupFullscreen.value) {
    style.width = "100%";
    style.height = "100%";
    style.minHeight = "100%";
    style.maxHeight = "none";
    style.borderRadius = "0";
  } else if (inPopupFrame && isPopupFullHeight.value) {
    const viewportH = popupCardMaxHeight.value;
    style.width = `${popupCardWidth.value}px`;
    style.height = `${viewportH}px`;
    style.minHeight = `${viewportH}px`;
    style.maxHeight = `${viewportH}px`;
    style.borderRadius = "0";
  }

  return style;
});

const canvasRootStyle = computed(() => {
  if (popupOverlayBackdrop.value) {
    return popupFrameFillsContainer.value
      ? { width: "100%" }
      : { width: `${popupFrameWidth.value}px` };
  }
  return showStepNav.value ? { width: `${viewportWidth.value}px` } : undefined;
});

// Canvas dark mode preview: simulates how the email will appear in recipients'
// dark-themed email clients. Uses CSS filter inversion — independent of the
// editor UI theme (light/dark/auto) which is controlled via uiTheme config.
const canvasStyle = computed(() => {
  // When a popup background image is showing, the outer wrapper paints it.
  // Keep this inner layer transparent so the artwork stays visible behind
  // block content; otherwise fall back to the template background colour.
  const popupDesign = popupOverlayBackdrop.value;
  const hasPopupBgImage = Boolean(
    popupDesign?.backgroundImage && popupDesign.backgroundImageUrl,
  );
  return {
    backgroundColor: hasPopupBgImage
      ? "transparent"
      : composeBackgroundColor(
          props.content.settings.backgroundColor,
          props.content.settings.backgroundOpacity,
        ),
    fontFamily: props.content.settings.fontFamily,
  };
});

const popupCanvasPaddingStyle = computed(() => {
  if (!props.content.settings.popup) {
    return {};
  }
  const pad = resolvePopupEmbed(props.content.settings).design.contentPadding;
  return {
    paddingTop: `${pad.top}px`,
    paddingRight: `${pad.right}px`,
    paddingBottom: `${pad.bottom}px`,
    paddingLeft: `${pad.left}px`,
  };
});

const popupOverlayImagePreview = computed(() => {
  const d = popupOverlayBackdrop.value;
  return Boolean(d?.overlayImage && d.overlayImageUrl);
});

/**
 * Backdrop blur is applied in both modes (overlay-only and image), so users
 * can combine a background image with blur if they want.
 * Tint is only added in overlay-only mode for contrast.
 */
const popupBackdropScrimStyle = computed((): Record<string, string> => {
  const d = popupOverlayBackdrop.value;
  if (!d) return {};
  const blur = clampBlurPx(d.overlayBlur);
  const filter = blur > 0 ? `blur(${blur}px)` : "none";
  return {
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    backgroundColor: popupOverlayImagePreview.value
      ? "transparent"
      : "rgba(0, 0, 0, 0.1)",
  };
});

const popupOverlayImageUrl = computed(
  () => popupOverlayBackdrop.value?.overlayImageUrl ?? "",
);

const popupOverlayImageLayerOpacity = computed(() =>
  clamp01(popupOverlayBackdrop.value?.overlayImageOpacity ?? 1),
);

/**
 * Overlay image inherits the same `overlayBlur` as the backdrop scrim so the
 * artwork itself gets blurred alongside the page behind it.
 */
const popupOverlayImageStyle = computed((): Record<string, string | number> => {
  const blur = clampBlurPx(popupOverlayBackdrop.value?.overlayBlur);
  const filter = blur > 0 ? `blur(${blur}px)` : "none";
  return {
    opacity: popupOverlayImageLayerOpacity.value,
    filter,
    WebkitFilter: filter,
  };
});

// ---------------------------------------------------------------------------
// Popup open-animation preview
// ---------------------------------------------------------------------------
// Mirrors the runtime behaviour in packages/editor/index.html:
//   - `fadeIn`  → single keyframe regardless of position
//   - `slideIn` → translate from a small offset matching the chosen position
//   - `smooth`  → translate + slight scale, again driven by position
//   - `none`    → no animation
// The animation origin is implicit: the card sits at its final flex-aligned
// position and the keyframe starts it offset toward the picked corner/edge,
// so it "appears" from that corner. We re-trigger the animation whenever the
// user changes `displayAnimation` or `popupPosition` by briefly setting
// `animation: none` (forces a reflow) before reapplying the real name.

const POPUP_ANIMATION_DURATION = "0.55s";
const POPUP_ANIMATION_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
/**
 * Small head-start so the popup briefly holds its "from" keyframe state before
 * sliding/fading in. `animation-fill-mode: both` (set in the shorthand below)
 * keeps the element at the offset/opacity-0 position during this delay so it
 * does not flash at its final position first.
 */
const POPUP_ANIMATION_DELAY = "0.18s";

function getPopupAnimationName(
  animation: PopupDisplayAnimation,
  position: PopupScreenPosition,
): string {
  if (animation === "none") return "";
  if (animation === "fadeIn") return "tpl-popup-fadeIn";

  const isSmooth = animation === "smooth";
  switch (position) {
    case "topLeft":
      return isSmooth ? "tpl-popup-smoothTopLeft" : "tpl-popup-topLeft";
    case "topCenter":
      return isSmooth ? "tpl-popup-smoothTop" : "tpl-popup-topToBottom";
    case "topRight":
      return isSmooth ? "tpl-popup-smoothTopRight" : "tpl-popup-topRight";
    case "middleLeft":
      return isSmooth ? "tpl-popup-smoothLeft" : "tpl-popup-leftToRight";
    case "middleRight":
      return isSmooth ? "tpl-popup-smoothRight" : "tpl-popup-rightToLeft";
    case "bottomLeft":
      return isSmooth ? "tpl-popup-smoothBottomLeft" : "tpl-popup-bottomLeft";
    case "bottomCenter":
      return isSmooth ? "tpl-popup-smoothBottom" : "tpl-popup-bottomToTop";
    case "bottomRight":
      return isSmooth ? "tpl-popup-smoothBottomRight" : "tpl-popup-bottomRight";
    case "center":
    default:
      return isSmooth ? "tpl-popup-smoothTop" : "tpl-popup-topToBottom";
  }
}

const popupAnimationName = computed(() => {
  const d = popupOverlayBackdrop.value;
  if (!d) return "";
  return getPopupAnimationName(d.displayAnimation, d.popupPosition);
});

/**
 * Toggles to `false` briefly when the animation should restart, then back to
 * `true` on the next tick so Vue emits two distinct `animation` values and the
 * browser actually replays the keyframe.
 */
const popupAnimationPlaying = ref(true);

watch(
  () => {
    const d = popupOverlayBackdrop.value;
    if (!d) return "";
    return `${d.displayAnimation}|${d.popupPosition}`;
  },
  async () => {
    popupAnimationPlaying.value = false;
    await nextTick();
    popupAnimationPlaying.value = true;
  },
);

const popupAnimationStyle = computed((): Record<string, string> => {
  if (!popupOverlayBackdrop.value) return {};
  const name = popupAnimationName.value;
  if (!name || !popupAnimationPlaying.value) {
    return { animation: "none" };
  }
  return {
    animation: `${name} ${POPUP_ANIMATION_DURATION} ${POPUP_ANIMATION_EASING} ${POPUP_ANIMATION_DELAY} both`,
  };
});

function handleCanvasClick(event: MouseEvent): void {
  if (props.previewMode) {
    return;
  }
  if (event.target === event.currentTarget) {
    emit("select-block", null);
  }
}

function getBlockComponent(block: Block): Component | null {
  return resolveBlockComponent(block, blockRegistry, blockComponentMap);
}

function getBlockLock(blockId: string): Collaborator | null {
  return props.lockedBlocks?.get(blockId) ?? null;
}

function handleFetchData(
  block: Block,
  payload: {
    fieldValues: Record<string, unknown>;
    dataSourceFetched: boolean;
  },
): void {
  if (block.type !== "custom") {
    return;
  }

  editor.updateBlock(block.id, {
    fieldValues: payload.fieldValues,
    dataSourceFetched: payload.dataSourceFetched,
  } as Partial<CustomBlockType>);
}
</script>

<template>
  <div
    class="tpl-canvas-root tpl:box-border tpl:max-w-full"
    :class="
      popupOverlayBackdrop ? 'tpl:flex tpl:min-h-0 tpl:flex-1 tpl:flex-col' : ''
    "
    :style="canvasRootStyle"
  >
    <div
      :class="
        popupOverlayBackdrop
          ? 'tpl-popup-frame tpl:relative tpl:flex tpl:flex-col tpl:max-w-full tpl:overflow-hidden'
          : 'tpl:contents'
      "
      :style="popupOverlayBackdrop ? popupFrameStyle : undefined"
    >
      <div
        v-if="popupOverlayBackdrop"
        class="tpl:pointer-events-none tpl:absolute tpl:inset-0 tpl:z-0 tpl:overflow-hidden"
        aria-hidden="true"
      >
        <div
          class="tpl:absolute tpl:inset-0"
          :style="popupBackdropScrimStyle"
        />
        <img
          v-if="popupOverlayImagePreview"
          :src="popupOverlayImageUrl"
          alt=""
          class="tpl:absolute tpl:inset-0 tpl:h-full tpl:w-full tpl:object-cover"
          :style="popupOverlayImageStyle"
        />
      </div>
      <div
        :class="
          popupOverlayBackdrop
            ? `tpl:relative tpl:z-[1] tpl:flex tpl:h-full tpl:min-h-0 tpl:flex-1 tpl:w-full ${popupCardAlignClass}`
            : 'tpl:contents'
        "
      >
        <div
          data-testid="canvas-wrapper"
          role="region"
          :aria-label="t.landmarks.canvas"
          class="tpl-canvas-wrapper tpl:rounded-lg"
          :class="
            isPopupViewportSized
              ? 'tpl:flex tpl:h-full tpl:min-h-0 tpl:flex-col !tpl:rounded-none'
              : 'tpl:shrink-0'
          "
          :style="[canvasCardStyle, popupAnimationStyle]"
        >
          <div
            class="tpl-canvas tpl:rounded-lg"
            :class="{
              'tpl-canvas--dark-mode': darkMode,
              'tpl-preview-mode': previewMode,
              'tpl:min-h-0 tpl:flex-1 tpl:overflow-y-auto':
                isPopupViewportSized,
              '!tpl:rounded-none': isPopupViewportSized,
            }"
            :style="{ ...canvasStyle, ...popupCanvasPaddingStyle }"
            @click="handleCanvasClick"
          >
            <draggable
              v-model="blocks"
              group="blocks"
              item-key="id"
              :animation="150"
              ghost-class="tpl-ghost"
              drag-class="tpl-dragging"
              handle=".tpl-block-btn"
              :invert-swap="true"
              :inverted-swap-threshold="0.65"
              :disabled="previewMode"
              class="tpl-canvas-blocks"
            >
              <template #item="{ element: block }">
                <div v-show="!conditionPreview?.isHidden(block.id)">
                  <div class="tpl:relative">
                    <!-- Collaboration lock overlay -->
                    <div
                      v-if="getBlockLock(block.id)"
                      class="tpl-collab-lock tpl:pointer-events-none tpl:absolute tpl:inset-0 tpl:z-[4] tpl:rounded-sm"
                      :style="{
                        outline: `2px solid ${getBlockLock(block.id)!.color}`,
                        outlineOffset: '-1px',
                      }"
                    >
                      <span
                        class="tpl:absolute tpl:-top-0.5 tpl:left-1/2 tpl:z-[5] tpl:flex tpl:-translate-x-1/2 tpl:-translate-y-full tpl:items-center tpl:gap-1 tpl:rounded-full tpl:px-2 tpl:py-0.5 tpl:text-[10px] tpl:font-medium tpl:whitespace-nowrap"
                        :style="{
                          backgroundColor: getBlockLock(block.id)!.color,
                          color: readableTextColor(
                            getBlockLock(block.id)!.color,
                          ),
                        }"
                      >
                        <span
                          class="tpl:inline-flex tpl:size-3 tpl:items-center tpl:justify-center tpl:rounded-full tpl:text-[8px] tpl:font-bold"
                          style="
                            background-color: color-mix(
                              in srgb,
                              var(--tpl-bg) 30%,
                              transparent
                            );
                          "
                        >
                          {{ getBlockLock(block.id)!.name.charAt(0) }}
                        </span>
                        {{ getBlockLock(block.id)!.name }}
                      </span>
                    </div>
                    <BlockWrapper
                      :block="block"
                      :is-selected="
                        !previewMode &&
                        selectedBlockId === block.id &&
                        !getBlockLock(block.id)
                      "
                      :viewport="viewport"
                      :preview-mode="previewMode"
                      @select="
                        previewMode || getBlockLock(block.id)
                          ? undefined
                          : emit('select-block', block.id)
                      "
                    >
                      <component
                        :is="getBlockComponent(block)"
                        :block="block"
                        :viewport="viewport"
                        @fetch-data="handleFetchData(block, $event)"
                        @update="
                          (updates: Partial<Block>) =>
                            editor.updateBlock(block.id, updates)
                        "
                      />
                    </BlockWrapper>
                  </div>
                </div>
              </template>
              <template #footer>
                <div
                  v-if="blocks.length === 0 && !previewMode"
                  class="tpl-canvas-empty tpl:m-6 tpl:flex tpl:min-h-[400px] tpl:flex-col tpl:items-center tpl:justify-center tpl:rounded-xl tpl:border-2 tpl:border-dashed tpl:px-10 tpl:py-12 tpl:text-center tpl:border-[var(--tpl-primary)] tpl:bg-[var(--tpl-bg-elevated)] tpl:font-[var(--tpl-font-family)]"
                >
                  <div
                    class="tpl-canvas-empty-icon tpl:mb-4 tpl:text-[var(--tpl-primary)]"
                  >
                    <SquarePlus :size="48" :stroke-width="1" />
                  </div>
                  <p
                    class="tpl-canvas-empty-title tpl:m-0 tpl:mb-2 tpl:text-base tpl:font-semibold tpl:text-[var(--tpl-primary)]"
                  >
                    {{ t.canvas.noBlocks }}
                  </p>
                  <p
                    class="tpl-canvas-empty-text tpl:m-0 tpl:text-sm tpl:text-[var(--tpl-text-dim)]"
                  >
                    {{ t.canvas.dragHint }}
                  </p>
                  <p
                    v-if="canUseAiChat && cloudT"
                    class="tpl:m-0 tpl:mt-2 tpl:flex tpl:flex-wrap tpl:items-center tpl:justify-center tpl:gap-x-1 tpl:gap-y-0.5 tpl:text-sm tpl:text-[var(--tpl-text-dim)]"
                  >
                    {{ t.canvas.aiHintChat }}
                    <button
                      class="tpl:inline-flex tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:gap-1 tpl:whitespace-nowrap tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:px-2 tpl:py-0.5 tpl:text-sm tpl:font-semibold tpl:transition-colors tpl:duration-150 tpl:bg-[var(--tpl-primary-light)] tpl:text-[var(--tpl-primary-hover)]"
                      @click="emit('open-ai-chat')"
                    >
                      <Sparkles :size="14" :stroke-width="2" />
                      {{ cloudT.aiMenu.aiAssistant }}
                    </button>
                    {{ t.canvas.aiHintChatSuffix }}
                  </p>
                  <p
                    v-if="canUseDesignToTemplate && cloudT"
                    class="tpl:m-0 tpl:mt-4 tpl:flex tpl:flex-wrap tpl:items-center tpl:justify-center tpl:gap-x-1 tpl:gap-y-0.5 tpl:text-sm tpl:text-[var(--tpl-text-dim)]"
                  >
                    {{ t.canvas.aiHintDesign }}
                    <button
                      class="tpl:inline-flex tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:gap-1 tpl:whitespace-nowrap tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:px-2 tpl:py-0.5 tpl:text-sm tpl:font-semibold tpl:transition-colors tpl:duration-150 tpl:bg-[var(--tpl-primary-light)] tpl:text-[var(--tpl-primary-hover)]"
                      @click="emit('open-design-reference')"
                    >
                      <ImageUp :size="14" :stroke-width="2" />
                      {{ cloudT.aiMenu.designToTemplate }}
                    </button>
                    {{ t.canvas.aiHintDesignSuffix }}
                  </p>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tpl-canvas-step {
  border-color: var(--tpl-border);
  background-color: color-mix(in srgb, var(--tpl-bg) 92%, transparent);
}

.tpl-canvas-step--active {
  border-color: color-mix(in srgb, var(--tpl-text) 55%, var(--tpl-border));
  background-color: var(--tpl-bg);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--tpl-text) 12%, transparent);
}

/* Counter-invert images so they look normal in dark mode */
.tpl-canvas--dark-mode :deep(img) {
  filter: invert(1) hue-rotate(180deg);
}

/* Counter-invert editor UI chrome so controls stay readable */
.tpl-canvas--dark-mode :deep(.tpl-block-actions),
.tpl-canvas--dark-mode :deep(.tpl-condition-toggle),
.tpl-canvas--dark-mode :deep(.tpl-block-hidden-overlay) {
  filter: invert(1) hue-rotate(180deg);
}
</style>

<!--
  Popup open-animation keyframes. These are intentionally unscoped because the
  animation name is referenced from an inline `:style` binding, which would not
  resolve Vue's scoped-style suffixes. Mirrors the keyframes shipped with the
  runtime popup embed in `packages/editor/index.html`.
-->
<style>
@keyframes tpl-popup-fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes tpl-popup-topToBottom {
  from {
    opacity: 0;
    transform: translateY(-90px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes tpl-popup-bottomToTop {
  from {
    opacity: 0;
    transform: translateY(90px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes tpl-popup-leftToRight {
  from {
    opacity: 0;
    transform: translateX(-90px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes tpl-popup-rightToLeft {
  from {
    opacity: 0;
    transform: translateX(90px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes tpl-popup-topLeft {
  from {
    opacity: 0;
    transform: translate(-90px, -90px);
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

@keyframes tpl-popup-topRight {
  from {
    opacity: 0;
    transform: translate(90px, -90px);
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

@keyframes tpl-popup-bottomLeft {
  from {
    opacity: 0;
    transform: translate(-90px, 90px);
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

@keyframes tpl-popup-bottomRight {
  from {
    opacity: 0;
    transform: translate(90px, 90px);
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

@keyframes tpl-popup-smoothTop {
  from {
    opacity: 0;
    transform: translateY(-40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes tpl-popup-smoothBottom {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes tpl-popup-smoothLeft {
  from {
    opacity: 0;
    transform: translateX(-40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes tpl-popup-smoothRight {
  from {
    opacity: 0;
    transform: translateX(40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes tpl-popup-smoothTopLeft {
  from {
    opacity: 0;
    transform: translate(-40px, -40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes tpl-popup-smoothTopRight {
  from {
    opacity: 0;
    transform: translate(40px, -40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes tpl-popup-smoothBottomLeft {
  from {
    opacity: 0;
    transform: translate(-40px, 40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes tpl-popup-smoothBottomRight {
  from {
    opacity: 0;
    transform: translate(40px, 40px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}
</style>
